import { PlayerConfig, EtvConfig, AvailableLanguages, Clip } from '@/interfaces/player-config.interface';

import { etvService } from '../services/etv.service';

export class ConfigService {
  private customStyleAdded: boolean = false;

  private playerConfig: PlayerConfig = {
    live: false,
    parentUrl: '',
    publisherId: '',
    sourceId: '',
    token: '',
    language: 'en',
    mute: true,
    previewImage: '',
    autoplay: false,
    accountId: '',
    advertisment: {
      enabled: false,
      adserver: '',
    },
  };

  public async getConfig(parentUrl: string, userConfig: any, etvConfig: EtvConfig): Promise<PlayerConfig> {
    this.playerConfig.parentUrl = parentUrl;
    this.playerConfig.token = userConfig.token;

    // live stream config ################################################

    if (etvConfig.streamUris && etvConfig.streamUris.hls) {
      this.playerConfig.stream = etvConfig.streamUris.hls;

      /*
       * Provide embedding website domain as header
       * https://develop.earthtv.com/jira/browse/WV-47
       */
      this.playerConfig.stream += '&domain=' + parentUrl;
      this.playerConfig.live = true;

      // live location
      if (etvConfig.template.overlay && etvConfig.template.overlay.enabled && etvConfig.source && etvConfig.source.location) {
        this.playerConfig.location = etvConfig.source.location;
      }
    }

    // playlist stream config ################################################
    // https://github.com/silvermine/videojs-quality-selector
    if (etvConfig.playlist) {
      const response = await etvService.getPlaylist(etvConfig.playlist.id, userConfig.token, userConfig.query, userConfig.sortField, userConfig.sortOrder, userConfig.limit, userConfig.offset);
      const playlist = response.data.results.map((clip: Clip) => {
        return {
          sources: this.getVariants(clip),
          poster: `${clip.medium.mediaUrls.previewImage.cdn}&w=${window.innerWidth}&q=70`,
          location: clip.source.location,
          time: clip.medium.startTime,
        };
      });

      this.playerConfig.playlist = playlist;
      this.playerConfig.live = false;
    }

    // general config ################################################

    /**
     * Use language for url param
     * if not defined use default en
     */
    if (userConfig.language && AvailableLanguages[userConfig.language]) {
      this.playerConfig.language = userConfig.language;
    } else {
      this.playerConfig.language = AvailableLanguages.en;
    }

    // playback
    if (etvConfig.template.playback) {
      // mute
      this.playerConfig.mute = userConfig.mute ? JSON.parse(userConfig.mute) : etvConfig.template.playback.mute;

      if (etvConfig.template.playback.limit) {
        this.playerConfig.stopLiveStreamInSec = etvConfig.template.playback.limit; // in seconds
      }

      // https://develop.earthtv.com/jira/browse/WV-21
      this.playerConfig.autoplay = userConfig.autoplay ? JSON.parse(userConfig.autoplay) : etvConfig.template.playback.autoplay;
      if (this.playerConfig.autoplay) {
        // mute player if autoplay
        this.playerConfig.mute = true;
      }
    }

    if (userConfig.previewImage) {
      this.playerConfig.previewImage = userConfig.previewImage;
    } else {
      this.playerConfig.previewImage = this.getPreviewImage(etvConfig);
    }

    if (etvConfig.template.accountId) {
      this.playerConfig.accountId = etvConfig.template.accountId;
    }

    // Custom Style
    if (etvConfig.template.assets && etvConfig.template.assets.css) {
      this.addCustomStyle(etvConfig.template.assets.css.urls.cdn);
    }

    // Custom logo
    if (etvConfig.template.assets && etvConfig.template.assets.logo && etvConfig.template.assets.logo.urls.cdn) {
      this.playerConfig.logo = etvConfig.template.assets.logo.urls.cdn;
    }

    // advertisement
    // TODO: add preroll, midroll, postrll, powerlink
    if (
      etvConfig.template.advertisement &&
      etvConfig.template.advertisement.preroll &&
      etvConfig.template.advertisement.preroll.enabled &&
      etvConfig.template.advertisement.adserver
    ) {
      this.playerConfig.advertisment.enabled = true;
      this.playerConfig.advertisment.adserver = etvConfig.template.advertisement.adserver;
    }

    if (etvConfig.publisher && etvConfig.publisher.id) {
      this.playerConfig.publisherId = etvConfig.publisher.id;
    }

    if (etvConfig.source && etvConfig.source.id) {
      this.playerConfig.sourceId = etvConfig.source.id;
    }

    // https://develop.earthtv.com/jira/browse/WV-46
    if (etvConfig.source && etvConfig.source.maintenance) {
      this.playerConfig.maintenance = true;
    }

    return this.playerConfig;
  }

  public async validateQuery(query: any) {
    if (query.token) {
      return true;
    } else {
      return false;
    }
  }

  private getVariants(clip: Clip) {
    let variants = [];
    const orig = clip.medium.sourceVariantId;
    let index = 0;
    let selectedIndex = 0;
    let previousBitrate = 0;

    // prepare variants
    for (const variant of clip.variants) {
      let label = variant.videoHeight + 'p';
      if (variant.sourceVariantId) {
        label += ' original';
      } else if (variant.id === orig) {
        label += ' original';
      } else {
        label += ' ' + (variant.videoBitrate / 1000000).toFixed(2) + ' MBit';
      }

      if (variant.videoHeight === 720 && variant.videoBitrate > previousBitrate) {
        selectedIndex = index;
        previousBitrate = variant.videoBitrate;
      }

      const src = {
        src: variant.urls.cdn,
        type: 'video/mp4',
        bitrate: variant.videoBitrate,
        hight: variant.videoHeight,
        label,
        selected: false,
      };
      variants.push(src);
      index++;
    }

    variants[selectedIndex].selected = true;
    // sort by bitrate
    variants = [...variants].sort((a, b) => b.bitrate - a.bitrate);

    return variants;
  }

  private addCustomStyle(path: string) {
    if (!this.customStyleAdded) {
      this.customStyleAdded = true;
      const link = document.createElement('link');
      link.href = path;
      link.type = 'text/css';
      link.rel = 'stylesheet';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }

  private getPreviewImage(config: EtvConfig) {
    if (config.stream) {
      if (config.stream.mediaUrls.previewImage.cdn) {
        return config.stream.mediaUrls.previewImage.cdn;
      } else if (config.stream.mediaUrls.previewImage.edge) {
        return config.stream.mediaUrls.previewImage.edge;
      } else {
        return ''; // TODO: add default preview image
      }
    } else {
      if (this.playerConfig.playlist && this.playerConfig.playlist.length) {
        // @ts-ignore
        return this.playerConfig.playlist[0].poster ? this.playerConfig.playlist[0].poster : '';
      } else {
        return '';
      }
    }
  }
}

export const configService = new ConfigService();
